import React, { Fragment, useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { FovModal, SpinAnimation } from 'components/generic';
import {
  CheckCircleOutlined,
  LockOutlined,
  Reload,
  QuestionCircleOutlined,
  DownloadOutlined,
} from 'components/icons';
import { useFormatPolling } from 'components/hooks';
import { customStartCase, getFormatState, FORMAT_STATES, UNPUBLISHABLE_FORMATS } from './utils';
import {
  ModalSubtitle,
  ScrollArea,
  SectionLabel,
  SectionHint,
  SectionDivider,
  FormatRow,
  FormatIcon,
  FormatName,
  StatusBadge,
  RowActions,
  RowButton,
  RowError,
  RowNote,
  EmptyState,
  ModalFooter,
  FooterLink,
  CancelButton,
} from './DownloadFormatsStyles';

const DOCS_PATH = '/documentation/downloading-model';
const ROW_ICON_SIZE = 20;

const DownloadFormatsModal = withTheme(props => {
  const { visible, formats, rpcGuid, onClose, onDownload, onPublish, canPublish, theme } = props;
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const [ publishStates, setPublishStates ] = useState({});
  const [ downloading, setDownloading ] = useState({});
  const [ selectedFormat, setSelectedFormat ] = useState(null);
  const [ polledFormats, setPolledFormats ] = useState(null);
  const currentFormats = polledFormats ?? formats ?? {};

  // Server state wins whenever it reports on the conversion, so a row self-corrects once the
  // worker starts emitting QUEUED / PROCESSING / FAILED. Otherwise the latch below decides.
  const rowState = (format, availability) => {
    const serverState = getFormatState(availability);

    if (serverState !== FORMAT_STATES.publishable) return serverState;

    return publishStates[format] ?? FORMAT_STATES.publishable;
  };

  // A finished conversion briefly reports NOT AVAILABLE before the asset is registered. Latch a
  // publishing format until a terminal state arrives so it neither flashes back into
  // 'Not available yet' nor drops out of the poll, which would strand it there.
  useEffect(() => {
    setPublishStates(previous => {
      const next = { ...previous };

      Object.keys(currentFormats).forEach(format => {
        const serverState = getFormatState(currentFormats[format]);

        if (serverState === FORMAT_STATES.publishing) {
          next[format] = FORMAT_STATES.publishing;
        }

        if (serverState === FORMAT_STATES.available || serverState === FORMAT_STATES.failed) {
          delete next[format];
        }
      });

      const unchanged = Object.keys(next).length === Object.keys(previous).length
        && Object.keys(next).every(format => next[format] === previous[format]);

      return unchanged ? previous : next;
    });
  }, [ currentFormats ]);

  const rows = useMemo(() => {
    return Object.keys(currentFormats).map(format => ({
      format,
      state: rowState(format, currentFormats[format]),
    }));
  }, [ currentFormats, publishStates ]);

  // Without publishing rights the modal is a plain format picker: a viewer cannot act on a format
  // that is converting or unpublished, so only downloadable rows are listed. That also leaves
  // pendingFormats empty, so the poll below never starts a timer off a read-only page.
  const pendingFormats = canPublish
    ? rows.filter(({ state }) => state === FORMAT_STATES.publishing).map(({ format }) => format)
    : [];

  const { timedOut } = useFormatPolling({ rpcGuid, pendingFormats, onFormats: setPolledFormats });

  // A publishing format is on its way to being downloadable, so it sits in Available and swaps its
  // button in place when the conversion lands, rather than jumping between sections.
  const available = rows.filter(({ state }) => (
    state === FORMAT_STATES.available || (canPublish && state === FORMAT_STATES.publishing)
  ));
  const unavailable = !canPublish ? [] : rows.filter(({ format, state }) => (
    (state === FORMAT_STATES.publishable || state === FORMAT_STATES.failed)
      && !UNPUBLISHABLE_FORMATS.includes(format.toUpperCase())
  ));

  const handlePublish = format => {
    setPublishStates(previous => ({ ...previous, [format]: FORMAT_STATES.publishing }));

    onPublish(format, {
      onError: () => setPublishStates(previous => ({ ...previous, [format]: FORMAT_STATES.failed })),
    });
  };

  // Each row tracks its own download so one in-flight format never blocks the others.
  const handleDownload = format => {
    setDownloading(previous => ({ ...previous, [format]: true }));

    onDownload(format, {
      onComplete: () => setDownloading(previous => ({ ...previous, [format]: false })),
    });
  };

  const buttonSpinner = (
    <SpinAnimation duration={2000}>
      <Reload width={14} height={14} />
    </SpinAnimation>
  );

  const renderAvailableRow = ({ format }) => {
    const isDownloading = downloading[format];

    return (
      <FormatRow
        key={format}
        theme={theme}
        $selectedTheme={selectedTheme}
        $selected={selectedFormat === format}
        $clickable={true}
        role='button'
        tabIndex={0}
        onClick={() => setSelectedFormat(format)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setSelectedFormat(format);
          }
        }}
      >
        <FormatIcon theme={theme} $selectedTheme={selectedTheme}>
          <CheckCircleOutlined width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
        </FormatIcon>

        <FormatName theme={theme} $selectedTheme={selectedTheme}>
          {customStartCase(format)}
        </FormatName>

        <RowActions>
          <StatusBadge theme={theme} $selectedTheme={selectedTheme}>
            Available
          </StatusBadge>

          <RowButton
            theme={theme}
            $selectedTheme={selectedTheme}
            disabled={isDownloading}
            onClick={event => {
              event.stopPropagation();
              handleDownload(format);
            }}
          >
            {isDownloading ? buttonSpinner : (
              <DownloadOutlined width={14} height={14} color={theme.colors[selectedTheme].accent} />
            )}
            {isDownloading ? 'Downloading…' : 'Download'}
          </RowButton>
        </RowActions>
      </FormatRow>
    );
  };

  const renderActionRow = ({ format, state }) => {
    const isPublishing = state === FORMAT_STATES.publishing;

    return (
      <FormatRow key={format} theme={theme} $selectedTheme={selectedTheme}>
        <FormatIcon theme={theme} $selectedTheme={selectedTheme} $muted={true}>
          {isPublishing ? (
            <SpinAnimation duration={2000}>
              <Reload width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
            </SpinAnimation>
          ) : (
            <LockOutlined width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
          )}
        </FormatIcon>

        <FormatName theme={theme} $selectedTheme={selectedTheme} $muted={true}>
          {customStartCase(format)}
        </FormatName>

        <RowActions>
          <RowButton
            theme={theme}
            $selectedTheme={selectedTheme}
            disabled={isPublishing}
            onClick={() => handlePublish(format)}
          >
            {isPublishing && buttonSpinner}
            {isPublishing && 'Publishing…'}
            {!isPublishing && (state === FORMAT_STATES.failed ? 'Retry' : 'Publish')}
          </RowButton>
        </RowActions>

        {state === FORMAT_STATES.failed && (
          <RowError theme={theme} $selectedTheme={selectedTheme}>
            Publishing failed. Try again.
          </RowError>
        )}

        {isPublishing && timedOut.includes(format) && (
          <RowNote theme={theme} $selectedTheme={selectedTheme}>
            Still processing — check back later.
          </RowNote>
        )}
      </FormatRow>
    );
  };

  return (
    <FovModal
      visible={visible}
      title='Select Download Format'
      onClose={onClose}
      width={620}
      headerSpace={5}
      noOverflow={true}
    >
      <ModalSubtitle theme={theme} $selectedTheme={selectedTheme}>
        {canPublish
          ? 'Choose an available format to download, or publish a new format first.'
          : 'Choose a format to download.'}
      </ModalSubtitle>

      {!available.length && !unavailable.length && (
        <EmptyState theme={theme} $selectedTheme={selectedTheme}>
          {canPublish
            ? 'No formats found, check permissions.'
            : 'No formats are available for download yet.'}
        </EmptyState>
      )}

      <ScrollArea>
        {available.length > 0 && (
          <Fragment>
            {/* With nothing to contrast it against, the heading is noise. */}
            {unavailable.length > 0 && (
              <SectionLabel theme={theme} $selectedTheme={selectedTheme}>
                Available
              </SectionLabel>
            )}

            {available.map(row => (
              row.state === FORMAT_STATES.available ? renderAvailableRow(row) : renderActionRow(row)
            ))}
          </Fragment>
        )}

        {unavailable.length > 0 && (
          <Fragment>
            {available.length > 0 && <SectionDivider theme={theme} $selectedTheme={selectedTheme} />}

            <SectionLabel theme={theme} $selectedTheme={selectedTheme}>
              Not available yet
            </SectionLabel>

            <SectionHint theme={theme} $selectedTheme={selectedTheme}>
              Publish a format to make it available for download.
            </SectionHint>

            {unavailable.map(renderActionRow)}
          </Fragment>
        )}
      </ScrollArea>

      <ModalFooter theme={theme} $selectedTheme={selectedTheme}>
        <FooterLink
          theme={theme}
          $selectedTheme={selectedTheme}
          href={DOCS_PATH}
          target='_blank'
          rel='noreferrer'
        >
          <QuestionCircleOutlined width={16} height={16} />
          Learn more about supported formats
        </FooterLink>

        <CancelButton theme={theme} $selectedTheme={selectedTheme} onClick={onClose}>
          Close
        </CancelButton>
      </ModalFooter>
    </FovModal>
  );
});

export { DownloadFormatsModal };
